import { supertypeClass, property } from '@haventech/amorphic';
import { CPQuoteRateClassDescriptions, LoanType } from '../../../js/interfaces/CoverpathTypes';
import { CoverpathUtils } from '../../../js/static/CoverpathUtils';
import { Customer } from '../../../../common/js/customer/Customer';
import { BaseQuote } from '../../../../common/js/quote/BaseQuote';
import { Assumptions } from '../../../../common/js/static/Assumptions';
import { CommonUtils } from '../../../../common/js/static/CommonUtils';
import { Product } from '../../../../common/js/static/Product';

@supertypeClass({ toClient: true, toServer: true })
export class CoverpathWholeLifeQuote extends BaseQuote {
  static readonly defaultTaxBracket = 32;

  @property({
    rule: ['numeric', 'required'],
    values: [65, 100],
    descriptions: {
      '100': '100 years',
      '65': '65 years',
    },
  })
  term: number;

  @property({
    comment: 'product code',
    rule: ['required'],
    values: ['CPL100', 'CPL65'],
    descriptions: {
      CPL100: 'Whole Life 100',
      CPL65: 'Whole Life 65',
    },
  })
  product: string;

  @property({
    comment: 'the type of product, whole',
  })
  productType: 'Whole' = 'Whole';

  @property({
    values: Object.keys(CPQuoteRateClassDescriptions),
    descriptions: CoverpathUtils.getRateClassDescriptions('longDescription'),
  })
  healthClass: string;

  @property({
    rule: 'required',
    values: CommonUtils.getSupportedStateCodes(Assumptions.coverpathUnsupportedStatesWhole),
    descriptions: CommonUtils.getSupportedStateValues(Assumptions.coverpathUnsupportedStatesWhole),
  })
  contractState: string;

  @property({
    rule: ['DOB', 'required'],
    validate: 'isDOBWithin(18,64)',
    sensitiveData: true,
    comment: 'Date of Birth of the Individual/Person',
  })
  dateOfBirth: Date;

  @property({
    comment: `amount of death benefit an individual can receive`,
    rule: ['currency', 'required'],
  })
  faceAmount: number;

  @property({
    toServer: false,
    rule: 'required',
  })
  ownerEqualsInsured: boolean;

  @property({
    rule: 'required',
  })
  carrierCode: string;

  @property({
    comment: `if agent start the draft application`,
  })
  isAgentAssistant: boolean;

  @property({
    comment: `dividend option selected by user`,
    values: ['PUA', 'Cash', 'PRPUA', 'PRCash', 'Accumulate'],
    descriptions: {
      PUA: 'Paid-up Additions',
      Cash: 'Cash',
      PRPUA: 'Reduce Premium/Paid-up Additions',
      PRCash: 'Reduce Premium/Cash',
      Accumulate: 'Dividend Accumulations',
    },
  })
  dividend: string;

  @property({
    rule: ['required', 'numericInt'],
    min: 0,
    max: 100,
    validate: 'isWithin(0, 100)',
    comment: 'Tax bracket tied to dividend options (Cash, RP/Cash, Accumulate)',
  })
  taxBracket: number;

  @property({ comment: 'dividend interest adjustment percent for a whole life policy' })
  dividendInterestAdjustment: number = 0;

  @property({
    comment: `Loan option selected by user`,
    values: ['Variable', 'Fixed'],
    descriptions: {
      Variable: 'Variable',
      Fixed: 'Fixed',
    },
  })
  loanType: LoanType;

  @property({
    min: 0.01,
    max: 10,
  })
  flatExtra: number;

  @property({
    comment: `Payment option selected by user`,
    values: ['Monthly', 'Annual'],
    descriptions: {
      Monthly: 'Monthly',
      Annual: 'Yearly',
    },
  })
  paymentFrequency: string;

  @property({ comment: 'Flag that notes if the premium should be shown to client or not' })
  showPremiumToClient: boolean;

  static new(customer: Customer, overrides?: Partial<CoverpathWholeLifeQuote>) {
    return new CoverpathWholeLifeQuote().init(customer, overrides);
  }

  init(customer: Customer, overrides?: Partial<CoverpathWholeLifeQuote>): CoverpathWholeLifeQuote {
    this.customer = customer;

    if (overrides) {
      this.updateProperties(overrides);
    }

    return this;
  }

  private updateProperties(overrides?: Partial<CoverpathWholeLifeQuote>) {
    if (overrides) {
      Object.keys(overrides).forEach((prop) => {
        this[prop] = overrides[prop];
      });
    }
  }

  public setTerm(): void {
    this.term = Product.productSpecs[this.product].term;
  }

  verifyProperties(): boolean {
    return (
      !!this.product &&
      !!this.term &&
      !!this.contractState &&
      this.__values__('contractState').indexOf(this.contractState) > -1 &&
      !!this.dateOfBirth &&
      !!this.gender &&
      !!this.healthClass &&
      !!this.faceAmount &&
      !!this.term &&
      !!this.paymentFrequency
    );
  }

  getTotalWaiverPremiumForRiders(): number {
    return this.riders.reduce(
      (total, rider) => total + (this.waiverOfPremium ? rider.premiumWaiver : 0),
      this.waiverOfPremiumAmount
    );
  }

  getTotalBasePremiumForRiders(): number {
    return this.riders.reduce((total, rider) => total + rider.premiumBase, 0);
  }

  getTotalPremium(): number {
    return this.getTotalWaiverPremiumForRiders() + this.premium;
  }

  isWholeLifeQuote(): boolean {
    return true;
  }
}
